import types from '../mutation-types';
import BotifySandbox from 'dashboard/api/botifySandbox';

const state = {
  uiFlags: {
    isCreatingSession: false,
    isSendingMessage: false,
  },
  session: null,
  messages: [],
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getMessages($state) {
    return $state.messages;
  },
  getSessionId($state) {
    return $state.session;
  },
};

export const actions = {
  sendMessage: async ({ commit, getters: $getters, dispatch, state }, message) => {
    const messageId = generateMessageId();
    commit(types.ADD_BOTIFY_SANDBOX_USER_MESSAGE, { message, id: messageId });

    try {
      let sessionId = $getters.getSessionId;
      if (!sessionId) {
        await dispatch('createSession');
        sessionId = $getters.getSessionId;
      }
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: true });

      const response = await BotifySandbox.sendMessage(sessionId, {
        content: message,
        timestamp: new Date().valueOf(),
      });

      // Update user message status to sent
      commit(types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS, {
        messageId,
        status: 'sent',
        errorMessage: null,
      });

      // Add bot response
      commit(types.ADD_BOTIFY_SANDBOX_BOT_MESSAGE, {
        message: response.data.content,
      });
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: false });
    } catch (error) {
      // Update user message status to failed
      commit(types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS, {
        messageId,
        status: 'failed',
        errorMessage: 'پیام ارسال نشد. دوباره تلاش کنید.',
      });
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: false });
      throw error;
    }
  },
  createSession: async ({ commit, rootGetters }) => {
    const botifyId = rootGetters['newBotify/getBotifyId'];
    commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isCreatingSession: true });
    try {
      const response = await BotifySandbox.createSession(botifyId);
      commit(types.SET_BOTIFY_SANDBOX_SESSION, { session: response.data.id });
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isCreatingSession: false });
    } catch (error) {
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isCreatingSession: false });
      throw error;
    }
  },
  resetSession: async ({ commit }) => {
    commit(types.SET_BOTIFY_SANDBOX_SESSION, { session: null });
    commit(types.CLEAR_BOTIFY_SANDBOX_MESSAGES);
  },
  retryMessage: async (
    { commit, getters: $getters, dispatch, state },
    { messageId }
  ) => {
    const message = state.messages.find(
      msg => msg.id === messageId && msg.type === 'USER'
    );

    if (!message) {
      return;
    }

    // Update status to sending and clear error
    commit(types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS, {
      messageId,
      status: 'sending',
      errorMessage: null,
    });

    try {
      let sessionId = $getters.getSessionId;
      if (!sessionId) {
        await dispatch('createSession');
        sessionId = $getters.getSessionId;
      }
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: true });

      const response = await BotifySandbox.sendMessage(sessionId, {
        content: message.content,
        timestamp: new Date().valueOf(),
      });

      // Update user message status to sent
      commit(types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS, {
        messageId,
        status: 'sent',
        errorMessage: null,
      });

      // Add bot response
      commit(types.ADD_BOTIFY_SANDBOX_BOT_MESSAGE, {
        message: response.data.content,
      });
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: false });
    } catch (error) {
      // Update user message status to failed
      commit(types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS, {
        messageId,
        status: 'failed',
        errorMessage: 'پیام ارسال نشد. دوباره تلاش کنید.',
      });
      commit(types.SET_BOTIFY_SANDBOX_UI_FLAG, { isSendingMessage: false });
      throw error;
    }
  },
};

// Helper function to generate unique message ID
function generateMessageId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export const mutations = {
  [types.SET_BOTIFY_SANDBOX_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_BOTIFY_SANDBOX_USER_MESSAGE]($state, { message, id }) {
    const messageId = id || generateMessageId();
    $state.messages = [
      ...$state.messages,
      {
        id: messageId,
        type: 'USER',
        content: message,
        status: 'sending',
        errorMessage: null,
      },
    ];
  },
  [types.ADD_BOTIFY_SANDBOX_BOT_MESSAGE]($state, { message }) {
    $state.messages = [
      ...$state.messages,
      {
        id: generateMessageId(),
        type: 'BOT',
        content: message,
      },
    ];
  },
  [types.SET_BOTIFY_SANDBOX_MESSAGE_STATUS](
    $state,
    { messageId, status, errorMessage }
  ) {
    const messageIndex = $state.messages.findIndex(
      msg => msg.id === messageId
    );
    if (messageIndex !== -1) {
      $state.messages[messageIndex] = {
        ...$state.messages[messageIndex],
        status,
        errorMessage: errorMessage || null,
      };
    }
  },
  [types.CLEAR_BOTIFY_SANDBOX_MESSAGES]($state) {
    $state.messages = [];
  },
  [types.SET_BOTIFY_SANDBOX_SESSION]($state, { session }) {
    $state.session = session;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
